import { useState, useCallback } from 'react';
import { Group, ActionIcon, Tooltip } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { X } from 'tabler-icons-react';
import dayjs from 'dayjs';
import { t } from 'i18next';

function getDay(date) {
  const d = date.getDay();
  return d === 0 ? 6 : d - 1; // lundi=0 ... dimanche=6
}

function startOfWeek(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() - getDay(date)
  );
}

function endOfWeek(date) {
  const monday = startOfWeek(date);
  return dayjs(monday).add(6, 'day').endOf('day').toDate(); // fin du dimanche
}

function sameWeek(a, b) {
  if (!a || !b) return false;
  return startOfWeek(a).getTime() === startOfWeek(b).getTime();
}

function isInWeekRange(date, ref) {
  if (!ref) return false;
  return date >= startOfWeek(ref) && date <= endOfWeek(ref);
}

export default function WeekCalendar({ handleDateChanged, attrName }) {
  const [hovered, setHovered] = useState(null); // pour le survol (highlight)
  const [value, setValue] = useState(null); // date sélectionnée

  const onChange = useCallback(
    (selectedDate) => {
      if (!selectedDate) return;
      setValue((prev) => {
        const next = prev && sameWeek(prev, selectedDate) ? null : selectedDate; // toggle même semaine = clear
        const firstDay = next ? startOfWeek(next) : null;
        handleDateChanged?.(null, attrName, firstDay);
        return next;
      });
    },
    [attrName, handleDateChanged]
  );

  const dayInRange = (date) => isInWeekRange(date, hovered);
  const dayFirstInRange = (date) => dayInRange(date) && getDay(date) === 0; // lundi
  const dayLastInRange = (date) => dayInRange(date) && getDay(date) === 6; // dimanche

  return (
    <Group position="center" spacing="xs">
      <Calendar
        value={value}
        firstDayOfWeek="monday"
        withCellSpacing={false}
        onChange={onChange}
        onDayMouseEnter={setHovered}
        onMouseLeave={() => setHovered(null)}
        isDateInRange={dayInRange}
        isDateFirstInRange={dayFirstInRange}
        isDateLastInRange={dayLastInRange}
      />
      {/* Bouton clear */}
      <Tooltip label={t('app.clear_value')}>
        <ActionIcon
          size="xs"
          variant="subtle"
          aria-label="Vider"
          onClick={() => {
            setValue(null);
            handleDateChanged?.(null, attrName, null);
          }}
        >
          <X size={14} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
